import { Box, Link, List } from '@material-ui/core'
import MuiCollapse from '@material-ui/core/Collapse'
import MuiListItem from '@material-ui/core/ListItem'
import MuiListItemIcon from '@material-ui/core/ListItemIcon'
import MuiListItemText from '@material-ui/core/ListItemText'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import React, { useEffect, useMemo, useState } from 'react'

import Color from '../base/Color'
import Icons, { IconName } from '../icons/Icons'

export interface MenuButton {
  icon?: IconName
  label: string
  isExpand?: boolean // @deprecated not used anymore
  url?: string
  subMenu?: MenuButton[]
}

export type MenuButtonsProps = {
  menus: MenuButton[]
  onClick?(
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number
  ): void // @deprecated soon unused
  urlOnClick?(
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    url?: string,
    expand?: boolean
  ): void // @deprecated soon unused
  activeRoute?: string
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      color: theme.palette.common.black,
      background: theme.palette.common.white,
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingRight: theme.spacing(3),
      marginLeft: theme.spacing(3),
      width: 290,
      position: 'relative',
      '&:hover': {
        background: 'transparent'
      }
    },
    itemSelected: {
      '&.Mui-selected': {
        backgroundColor: theme.palette.common.white,
        '&:hover': {
          background: 'transparent'
        }
      }
    },
    iconStyle: {
      color: theme.palette.common.black,
      minWidth: theme.spacing(0),
      marginRight: theme.spacing(2)
    },
    listTextPrimary: {
      ...theme.typography.subtitle1
    },
    lineSelected: {
      background: theme.palette.primary.main,
      width: theme.spacing(0.5),
      height: 24,
      position: 'absolute',
      left: 0,
      borderRadius: theme.spacing(1)
    },
    collpase: {
      background: Color.primary[300]
    },
    collapseItem: {
      color: theme.palette.common.black,
      borderBottom: `1px solid ${Color.primary[500]}`,
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(5.25),
      '&:hover': {
        background: 'transparent'
      }
    },
    linkSubMenu: {
      color: theme.palette.common.black,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'none'
      }
    },
    linkColorInfo: {
      color: theme.palette.info.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'none'
      }
    }
  })
)

interface ListItemProps {
  menu: MenuButton
  activeRoute: string
}

const ListItem: React.FC<ListItemProps> = ({ menu, activeRoute }) => {
  const classes = useStyles()
  const [isOpen, setOpen] = useState(false)

  const subMenuActive = useMemo(
    () => menu.subMenu && menu.subMenu.find((menu) => menu.url === activeRoute),
    [activeRoute]
  )

  useEffect(() => {
    if (!!subMenuActive && !isOpen) {
      setOpen(true)
    }
  }, [!!subMenuActive])

  return (
    <>
      <MuiListItem
        button
        disableRipple
        component={menu.subMenu ? 'li' : Link}
        onClick={() => {
          if (menu.subMenu) {
            setOpen(!isOpen)
          }
        }}
        href={menu.url ? `/${menu.url}` : undefined}
        classes={{ root: classes.root, selected: classes.itemSelected }}
      >
        {(activeRoute === menu.url || !!subMenuActive) && (
          <Box className={classes.lineSelected} />
        )}
        <MuiListItemIcon classes={{ root: classes.iconStyle }}>
          <Icons size="medium" iconName={menu.icon ?? 'MenuIcon'} />
        </MuiListItemIcon>
        <MuiListItemText
          classes={{ primary: classes.listTextPrimary }}
          primary={menu.label}
        />
        {menu.subMenu &&
          (isOpen ? (
            <Icons iconName="ArrowBottomIcon" size="small" />
          ) : (
            <Icons iconName="ArrowLeftIcon" size="small" />
          ))}
      </MuiListItem>
      {menu.subMenu && (
        <MuiCollapse in={isOpen} className={classes.collpase}>
          {menu.subMenu.map((menuSub, indexSub) => {
            return (
              <MuiListItem
                key={`sub-menu-${indexSub}`}
                button
                component={Link}
                classes={{ root: classes.collapseItem }}
                className={
                  activeRoute === menuSub.url
                    ? classes.linkColorInfo
                    : classes.linkSubMenu
                }
                href={`/${menuSub.url}`}
              >
                <MuiListItemText primary={menuSub.label} />
              </MuiListItem>
            )
          })}
        </MuiCollapse>
      )}
    </>
  )
}

const MenuButton: React.FC<MenuButtonsProps> = (props) => {
  const { menus, activeRoute } = props

  return (
    <List>
      {menus.map((menu, index) => (
        <ListItem key={index} menu={menu} activeRoute={activeRoute ?? ''} />
      ))}
    </List>
  )
}

export default MenuButton
